import { useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  INNER_SERIES_LIST_PAGE_PARAM,
  OUTER_SERIES_LIST_PAGE_PARAM,
  PREMIUM_SERIES_INNER_BASE,
  PREMIUM_TRANSLATION_FULL_PATH,
  PREMIUM_VIDEO_LIST_REFACTOR_BASE,
  QUICK_SERIES_INNER_BASE,
  QUICK_TRANSLATE_FULL_PATH,
} from "../../utils/menuPathNormalize";

function pathnameIsPremiumSeries(pathname: string): boolean {
  return (
    pathname === PREMIUM_SERIES_INNER_BASE || pathname.startsWith(`${PREMIUM_SERIES_INNER_BASE}/`)
  );
}

function pathnameIsQuickSeriesInner(pathname: string): boolean {
  return (
    pathname === QUICK_SERIES_INNER_BASE || pathname.startsWith(`${QUICK_SERIES_INNER_BASE}/`)
  );
}

function toInt(value: string | null): number {
  const n = Number.parseInt(value || "", 10);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * 页面级返回目标：按当前路由（视频列表 / 内层剧集页）计算返回链接，
 * 原是 PremiumWorkspaceLayout 顶栏 leading 的 backTo，Header 移除后由页面自行渲染。
 * 返回 null 表示当前路由不在可返回的页面集合内。
 */
export function useSeriesBackTarget(): string | null {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pathname = location.pathname;

  return useMemo(() => {
    if (pathname === PREMIUM_VIDEO_LIST_REFACTOR_BASE) {
      const q = new URLSearchParams();
      const seriesId = searchParams.get("seriesId");
      const seriesName = searchParams.get("seriesName");
      if (seriesId) q.set("seriesId", seriesId);
      if (seriesName) q.set("seriesName", seriesName);
      const langCode = searchParams.get("langCode");
      if (langCode) q.set("langCode", langCode);
      if (searchParams.get("from") === "quick") q.set("from", "quick");
      const innerP = toInt(searchParams.get(INNER_SERIES_LIST_PAGE_PARAM));
      if (Number.isFinite(innerP) && innerP > 1) {
        q.set("page", String(Math.floor(innerP)));
      }
      const outerP = toInt(searchParams.get(OUTER_SERIES_LIST_PAGE_PARAM));
      if (Number.isFinite(outerP) && outerP > 1) {
        q.set(OUTER_SERIES_LIST_PAGE_PARAM, String(Math.floor(outerP)));
      }
      const query = q.toString();
      if (searchParams.get("from") === "quick") {
        return query ? `${QUICK_SERIES_INNER_BASE}?${query}` : QUICK_SERIES_INNER_BASE;
      }
      return query ? `${PREMIUM_SERIES_INNER_BASE}?${query}` : PREMIUM_SERIES_INNER_BASE;
    }

    if (pathnameIsQuickSeriesInner(pathname)) {
      const outerP = toInt(searchParams.get(OUTER_SERIES_LIST_PAGE_PARAM));
      if (Number.isFinite(outerP) && outerP > 1) {
        return `${QUICK_TRANSLATE_FULL_PATH}?page=${Math.floor(outerP)}`;
      }
      return QUICK_TRANSLATE_FULL_PATH;
    }

    if (pathnameIsPremiumSeries(pathname)) {
      const outerP = toInt(searchParams.get(OUTER_SERIES_LIST_PAGE_PARAM));
      if (Number.isFinite(outerP) && outerP > 1) {
        return `${PREMIUM_TRANSLATION_FULL_PATH}?page=${Math.floor(outerP)}`;
      }
      return PREMIUM_TRANSLATION_FULL_PATH;
    }

    return null;
  }, [pathname, searchParams]);
}
